// S&OP Planning Types

export interface PlanningDataPoint {
  warehouse_id: string;
  week_date: string; // ISO format: "2024-W15"
  tenant_id?: string; // Optional for backward compatibility, auto-injected by backend
  inbound_quantity: number;
  outbound_quantity: number;
  starting_inventory: number;
  projected_inventory: number; // Calculated field
  data_source: string;
  last_updated: string;
  updated_by: string;
}

export interface TimeSeriesData {
  week_date: string;
  total_inbound: number;
  total_outbound: number;
  total_inventory: number;
  warehouse_count: number;
}

export interface Scenario {
  scenario_id: string;
  scenario_name: string;
  tenant_id?: string; // Optional for backward compatibility
  base_scenario_id?: string;
  assumptions: Record<string, any>;
  ai_generated: boolean;
  created_at: string;
  status: ScenarioStatus;
  planning_data: PlanningDataPoint[];
}

export enum ScenarioStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived'
}

export interface AIInsight {
  insight_id: string;
  tenant_id?: string; // Optional for backward compatibility
  insight_type: InsightType;
  severity: SeverityLevel;
  title: string;
  description: string;
  affected_warehouses: string[];
  affected_weeks: string[];
  ai_confidence: number;
  recommendations: string[];
  created_at: string;
  status: InsightStatus;
}

export enum InsightType {
  ALERT = 'alert',
  RECOMMENDATION = 'recommendation',
  TREND = 'trend'
}

export enum SeverityLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum InsightStatus {
  NEW = 'new',
  ACKNOWLEDGED = 'acknowledged',
  RESOLVED = 'resolved'
}

export interface NaturalLanguageQuery {
  query_id: string;
  session_id: string; // sam-chatbot session ID
  tenant_id?: string; // Optional for backward compatibility
  user_query: string;
  extracted_entities: Record<string, any>; // warehouses, weeks, metrics
  ai_response: string;
  planning_data_context: Record<string, any>;
  processing_time_ms: number;
  created_at: string;
}

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
  context?: Record<string, any>;
}

export interface ChatSession {
  session_id: string;
  tenant_id?: string; // Optional for backward compatibility
  messages: ChatMessage[];
  created_at: string;
  last_updated: string;
}

// API Request/Response Types
export interface PlanningDataRequest {
  warehouse_id?: string;
  start_week?: string;
  end_week?: string;
  tenant_id?: string; // Optional, auto-injected by backend middleware
}

export interface PlanningDataResponse {
  planning_data: PlanningDataPoint[];
  total_count: number;
  tenant_id?: string; // Included in multi-tenant responses
}

export interface BulkUpdateRequest {
  updates: PlanningDataPoint[];
}

export interface BulkUpdateResponse {
  updated_count: number;
  errors: string[];
}

export interface ScenarioRequest {
  scenario_name: string;
  base_scenario_id?: string;
  assumptions: Record<string, any>;
}

export interface ScenarioResponse {
  scenario: Scenario;
}

export interface ScenarioComparisonRequest {
  scenario_ids: string[];
  metrics: string[];
}

export interface ScenarioComparisonResponse {
  comparison_data: {
    scenario_id: string;
    scenario_name: string;
    metrics: Record<string, number>;
  }[];
}

export interface InsightsRequest {
  warehouse_ids?: string[];
  severity_levels?: SeverityLevel[];
  insight_types?: InsightType[];
  limit?: number;
}

export interface InsightsResponse {
  insights: AIInsight[];
  total_count: number;
}

export interface NLQueryRequest {
  query: string;
  session_id?: string;
  context?: Record<string, any>;
}

export interface NLQueryResponse {
  query_id: string;
  session_id: string;
  response: string;
  extracted_entities: Record<string, any>;
  planning_data_context: Record<string, any>;
  processing_time_ms: number;
}

export interface WarehouseListResponse {
  warehouses: string[];
  tenant_id?: string; // Included in multi-tenant responses
}

// Chart and Visualization Types
export interface ChartDataPoint {
  x: string | number | Date;
  y: number;
  label?: string;
  color?: string;
}

export interface ChartSeries {
  name: string;
  data: ChartDataPoint[];
  color: string;
  type?: 'line' | 'bar' | 'area';
}

export interface ChartConfig {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  series: ChartSeries[];
  width?: number;
  height?: number;
}